import { useState } from "react";
import {
  Control,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { Braces, Check, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { ConfirmationDialog } from "@/components/Dialogs/ConfirmationDialog";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { cn } from "@/lib/utils";
import type { WorkflowSettingsInput } from "@/generated";

export interface ExternalValue {
  name: string;
}

/** Rows past this many scroll instead of growing the settings page. */
const MAX_VISIBLE_ROWS = 6;
const ROW_HEIGHT_PX = 32; // h-8
const ROW_GAP_PX = 8; // space-y-2
/**
 * A focus ring is drawn outside the input's border box, and a scroller clips it
 * on both axes — `overflow-y: auto` forces `overflow-x` to compute to `auto`
 * too, so the ring on the row being renamed was cut off left and right. The
 * scroller is padded by the ring's width and pulled back out by a matching
 * negative margin, which leaves every row exactly where it was.
 */
const RING_SLACK_PX = 2; // p-0.5 / -m-0.5

/**
 * The name reaches the embed snippet as `window.<name>`, so it has to be a bare
 * JS identifier or the snippet it generates will not parse.
 */
const NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * `user` and `_`-prefixed names are platform-owned in `context_variables` (see
 * `is_reserved_variable_name`), and `NOXUS_`-prefixed ones would collide with
 * the widget's own globals in the snippet (`NOXUS_ASSISTANT_ID`, and
 * `NOXUS_EXTERNAL_VALUE_NAMES` itself).
 */
function isReservedName(name: string): boolean {
  return name === "user" || name.startsWith("_") || name.startsWith("NOXUS_");
}

export function validateExternalValueName(
  name: string,
  takenNames: string[]
): string | null {
  if (!name) return "Enter a name.";
  if (!NAME_PATTERN.test(name)) {
    return "Use only letters, numbers and underscores, starting with a letter.";
  }
  if (isReservedName(name)) return `"${name}" is a reserved name.`;
  if (takenNames.includes(name)) return `"${name}" already exists.`;
  return null;
}

/**
 * Points every flow input bound to `from` at `to`.
 *
 * A rename that only rewrites the declaration leaves `input_overrides` holding
 * the old string: the picker renders blank and the embed snippet stops listing
 * the value as in use, so the flow silently receives nothing.
 */
export function remapBoundFlowInputs(
  workflows: WorkflowSettingsInput[],
  from: string,
  to: string
): WorkflowSettingsInput[] {
  return workflows.map((workflow) => {
    const overrides = workflow.input_overrides;
    if (!overrides) return workflow;
    let changed = false;
    const next: NonNullable<typeof overrides> = {};
    for (const [input, override] of Object.entries(overrides)) {
      if (override?.mode === "variable" && override.value === from) {
        next[input] = { ...override, value: to };
        changed = true;
      } else {
        next[input] = override;
      }
    }
    return changed ? { ...workflow, input_overrides: next } : workflow;
  });
}

function NameEditor({
  draft,
  error,
  isDisabled,
  onDraftChange,
  onSave,
  onCancel,
}: {
  draft: string;
  error: string | null;
  isDisabled?: boolean;
  onDraftChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          aria-label="External value name"
          aria-invalid={!!error}
          className="flex-1"
          value={draft}
          placeholder="variable_name"
          disabled={isDisabled}
          onChange={(event) => onDraftChange(event.target.value)}
          onBlur={onCancel}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onSave();
            } else if (event.key === "Escape") {
              event.preventDefault();
              onCancel();
            }
          }}
        />
        <Button
          type="button"
          size="icon"
          className="shrink-0"
          aria-label="Save external value"
          disabled={isDisabled}
          // Cancelling on blur would otherwise fire before the click lands, so
          // the save button could never be clicked. Keeping focus on the input
          // also leaves the caret where it was when a name is rejected.
          onMouseDown={(event) => event.preventDefault()}
          onClick={onSave}
        >
          <Check className="h-3.5 w-3.5" />
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/**
 * One declared external value: the row shape from the table Columns list
 * (`ColumnsTab`), minus its drag handle, identifier badge and type label.
 *
 * Hover is tracked in React rather than with a `group-hover` variant: the
 * variant did not scope to the single hovered row here, so every row showed
 * its actions at once.
 */
function ExternalValueRow({
  value,
  isHovered,
  isDisabled,
  onHoverChange,
  onEdit,
  onDelete,
}: {
  value: ExternalValue;
  isHovered: boolean;
  isDisabled?: boolean;
  onHoverChange: (hovered: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const showActions = !isDisabled && isHovered;

  return (
    <div
      className="relative flex items-center w-full"
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <div
        className={cn(
          "flex-1 flex items-center gap-2 pl-2 pr-[0.1875rem] h-8 bg-background border border-border rounded-[4px] transition-colors",
          !isDisabled && "hover:border-accent"
        )}
      >
        <div className="flex-1 flex items-center justify-between min-w-0 h-full">
          <span className="truncate text-sm text-foreground">{value.name}</span>

          <div className="flex items-center gap-0.5 h-full relative min-w-[40px] justify-end">
            {showActions && (
              <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 bg-background pl-2">
                <Button
                  variant="ghost"
                  size="iconxs"
                  aria-label="Rename external value"
                  onClick={onEdit}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  variant="ghostDestructive"
                  size="iconxs"
                  aria-label="Delete external value"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The external-values editor: a heading with its add button, and the list.
 *
 * The enable toggle is its own setting above this, so the card carries no
 * switch and no description — only the list it exists to hold. Compact paddings
 * keep it from reading as a heavier section than the toggle above it.
 *
 * A name is only written to the form once it validates. Autosave persists
 * whatever the form holds, so seeding a blank row on "Add" left an unusable
 * declaration behind whenever the page was left mid-edit.
 */
export function ExternalValues({
  label = "External values list",
  value,
  onChange,
  onRename,
  isDisabled,
}: {
  label?: string;
  value: ExternalValue[];
  onChange: (next: ExternalValue[]) => void;
  onRename?: (from: string, to: string) => void;
  isDisabled?: boolean;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null
  );
  const pendingDeleteName =
    pendingDeleteIndex === null ? "" : (value[pendingDeleteIndex]?.name ?? "");

  const stopEditing = () => {
    setEditingIndex(null);
    setIsAdding(false);
    setDraft("");
    setError(null);
  };

  const startAdding = () => {
    setEditingIndex(null);
    setIsAdding(true);
    setDraft("");
    setError(null);
  };

  const startEditing = (index: number) => {
    setIsAdding(false);
    setEditingIndex(index);
    setDraft(value[index]?.name ?? "");
    setError(null);
  };

  const saveNew = () => {
    const name = draft.trim();
    const reason = validateExternalValueName(
      name,
      value.map((v) => v.name)
    );
    if (reason) {
      setError(reason);
      return;
    }
    onChange([...value, { name }]);
    stopEditing();
  };

  const saveRename = (index: number) => {
    const name = draft.trim();
    const previous = value[index]?.name ?? "";
    const reason = validateExternalValueName(
      name,
      value.filter((_, i) => i !== index).map((v) => v.name)
    );
    if (reason) {
      setError(reason);
      return;
    }
    onChange(value.map((v, i) => (i === index ? { ...v, name } : v)));
    if (previous && previous !== name) onRename?.(previous, name);
    stopEditing();
  };

  // Border-box sizing, so the slack padding has to be added on top or the last
  // visible row loses 4px and the list reads as cut off rather than scrollable.
  const maxHeight =
    MAX_VISIBLE_ROWS * ROW_HEIGHT_PX +
    (MAX_VISIBLE_ROWS - 1) * ROW_GAP_PX +
    RING_SLACK_PX * 2;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {!isDisabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={startAdding}
          >
            <Plus className="w-3.5 h-3.5" />
            Add external value
          </Button>
        )}
      </div>

      {value.length === 0 && !isAdding && (
        <NoxusAlert
          icon={Braces}
          description="No external values yet. Add one to map it to a flow input."
        />
      )}

      {value.length > 0 && (
        <div
          className="space-y-2 overflow-y-auto p-0.5 -m-0.5"
          style={{ maxHeight: `${maxHeight}px` }}
        >
          {value.map((externalValue, index) =>
            editingIndex === index ? (
              <NameEditor
                key={index}
                draft={draft}
                error={error}
                isDisabled={isDisabled}
                onDraftChange={setDraft}
                onSave={() => saveRename(index)}
                onCancel={stopEditing}
              />
            ) : (
              <ExternalValueRow
                key={index}
                value={externalValue}
                isHovered={hoveredIndex === index}
                isDisabled={isDisabled}
                onHoverChange={(hovered) =>
                  setHoveredIndex(hovered ? index : null)
                }
                onEdit={() => startEditing(index)}
                onDelete={() => setPendingDeleteIndex(index)}
              />
            )
          )}
        </div>
      )}

      {isAdding && (
        <NameEditor
          draft={draft}
          error={error}
          isDisabled={isDisabled}
          onDraftChange={setDraft}
          onSave={saveNew}
          onCancel={stopEditing}
        />
      )}

      <ConfirmationDialog
        isOpen={pendingDeleteIndex !== null}
        onOpenChange={(open) => !open && setPendingDeleteIndex(null)}
        title="Delete external value"
        description={
          pendingDeleteName
            ? `Delete "${pendingDeleteName}"? Any flow input bound to it will fall back to an empty value.`
            : ""
        }
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={() => {
          if (pendingDeleteIndex === null) return;
          onChange(value.filter((_, i) => i !== pendingDeleteIndex));
          setPendingDeleteIndex(null);
          stopEditing();
        }}
      />
    </div>
  );
}

export const ExternalValuesField = ({
  name,
  control,
  isDisabled,
  label,
}: {
  name: string;
  control: Control<Record<string, unknown>>;
  isDisabled?: boolean;
  label?: string;
}) => {
  // Mirror of the sibling path `WorkflowsFieldNCL` derives to read this field,
  // so a rename can follow through to the flow inputs bound to the old name.
  const workflowsFieldName = /\.variables$/.test(name)
    ? name.replace(/\.variables$/, ".workflows")
    : "workflows";

  const { field: variablesField } = useController({
    control,
    name,
    defaultValue: [],
  });
  // Read-only: `WorkflowsFieldNCL` owns that field's registration, so watch it
  // rather than registering a second controller for the same name — a second
  // one without its `defaultValue` can clobber the configured flows on mount.
  const workflows = useWatch({ control, name: workflowsFieldName }) as
    | WorkflowSettingsInput[]
    | undefined;
  const form = useFormContext();

  return (
    <ExternalValues
      label={label}
      value={(variablesField.value as ExternalValue[]) || []}
      onChange={variablesField.onChange}
      onRename={(from, to) => {
        if (!form || !workflows?.length) return;
        form.setValue(
          workflowsFieldName,
          remapBoundFlowInputs(workflows, from, to),
          // The agent form autosaves off dirty state, so the remapped
          // overrides have to be marked as a change or only the declaration
          // would persist.
          { shouldDirty: true }
        );
      }}
      isDisabled={isDisabled}
    />
  );
};
